import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaPlus, FaEdit, FaLink } from 'react-icons/fa';
import api from '../services/api';

const ManageUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showParentModal, setShowParentModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedParentId, setSelectedParentId] = useState('');
    const navigate = useNavigate();

    // Mapowanie user_id → user dla wyświetlania podmiotu nadrzędnego
    const userIdToUser = {};
    users.forEach(u => {
        userIdToUser[u.id] = u;
    });

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                setLoading(true);
                const response = await api.get('/users/');
                // Filtruj tylko użytkowników z rolą "user"
                setUsers(response.data.filter(u => u.role === 'user'));
                setError(null);
            } catch (error) {
                console.error('Failed to fetch users', error);
                setError('Nie udało się pobrać listy użytkowników');
            } finally {
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    const openParentModal = (user) => {
        setSelectedUser(user);
        setSelectedParentId(user.parent_user_id || '');
        setShowParentModal(true);
    };

    const handleSetParent = async () => {
        if (!selectedUser) return;

        try {
            const params = selectedParentId ? `?parent_user_id=${selectedParentId}` : '';
            const response = await api.post(`/users/${selectedUser.id}/set-parent${params}`);

            // Aktualizuj lokalny stan
            setUsers(users.map(u =>
                u.id === selectedUser.id ? { ...u, parent_user_id: response.data.parent_user_id } : u
            ));
            setShowParentModal(false);
            setSelectedUser(null);
        } catch (error) {
            console.error('Failed to set parent', error);
            alert('Błąd podczas ustawiania podmiotu nadrzędnego');
        }
    };

    // Członek ma NIP albo — jako osoba fizyczna bez działalności — tylko PESEL
    const taxIdentity = (user) => {
        if (user.tax_id_number) return user.tax_id_number;
        if (user.pesel) return `PESEL ${user.pesel}`;
        return '-';
    };

    const VatExemptBadge = ({ user }) =>
        user.vat_exempt ? (
            <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700" title="Zwolniony z VAT (art. 113)">
                zw. VAT
            </span>
        ) : null;

    const getParentName = (parentUserId) => {
        if (!parentUserId) return '-';
        const parent = userIdToUser[parentUserId];
        return parent ? (parent.full_name || parent.username) : '-';
    };

    // Renderowanie karty dla widoku mobilnego
    const renderMobileUserList = () => {
        return users.map((user) => (
            <div key={user.username} className="mb-4 p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
                <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center">
                        <FaUser className="text-softTeal mr-2" />
                        <span className="font-bold">{user.username}</span>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => navigate(`/admin/users/edit/${user.username}`)}
                            className="text-softTeal hover:text-warmOrange p-1"
                            title="Edytuj"
                        >
                            <FaEdit />
                        </button>
                        <button
                            onClick={() => openParentModal(user)}
                            className="text-softTeal hover:text-warmOrange p-1"
                            title="Zmień podmiot nadrzędny"
                        >
                            <FaLink />
                        </button>
                    </div>
                </div>
                <div className="mb-2">
                    <span className="font-bold">Pełna nazwa:</span>
                    <span className="ml-2">{user.full_name || 'Brak'}</span>
                    <VatExemptBadge user={user} />
                </div>
                <div className="mb-2">
                    <span className="font-bold">{user.tax_id_number || !user.pesel ? 'NIP:' : 'Identyfikator:'}</span>
                    <span className="ml-2">{taxIdentity(user)}</span>
                </div>
                <div className="mb-2">
                    <span className="font-bold">Adres:</span>
                    <span className="ml-2">{user.address || 'Brak'}</span>
                </div>
                <div className="mb-2">
                    <span className="font-bold">Konto bankowe:</span>
                    <span className="ml-2">{user.bank_account_number || 'Brak'}</span>
                </div>
                <div className="mb-2">
                    <span className="font-bold">Podmiot nadrzędny:</span>
                    <span className="ml-2">{getParentName(user.parent_user_id)}</span>
                </div>
            </div>
        ));
    };

    // Renderowanie tradycyjnej tabeli dla większych ekranów
    const renderDesktopTable = () => {
        return (
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="border px-4 py-2 text-left">Login</th>
                        <th className="border px-4 py-2 text-left">Pełna nazwa</th>
                        <th className="border px-4 py-2 text-left">NIP / PESEL</th>
                        <th className="border px-4 py-2 text-left">Adres</th>
                        <th className="border px-4 py-2 text-left">Konto bankowe</th>
                        <th className="border px-4 py-2 text-left">Podmiot nadrzędny</th>
                        <th className="border px-4 py-2 text-center">Podgląd</th>
                    </tr>
                    </thead>
                    <tbody>
                    {users.map((user) => (
                        <tr key={user.username}>
                            <td className="border px-4 py-2">{user.username}</td>
                            <td className="border px-4 py-2">
                                {user.full_name || '-'}
                                <VatExemptBadge user={user} />
                            </td>
                            <td className="border px-4 py-2 whitespace-nowrap">{taxIdentity(user)}</td>
                            <td className="border px-4 py-2">{user.address || '-'}</td>
                            <td className="border px-4 py-2">{user.bank_account_number || '-'}</td>
                            <td className="border px-4 py-2">{getParentName(user.parent_user_id)}</td>
                            <td className="border px-4 py-2 text-center">
                                <div className="flex justify-center gap-2">
                                    <button
                                        onClick={() => navigate(`/admin/users/edit/${user.username}`)}
                                        className="text-softTeal hover:text-warmOrange p-1"
                                        title="Edytuj"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        onClick={() => openParentModal(user)}
                                        className="text-softTeal hover:text-warmOrange p-1"
                                        title="Zmień podmiot nadrzędny"
                                    >
                                        <FaLink />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // Modal do zmiany podmiotu nadrzędnego
    const renderParentModal = () => {
        if (!showParentModal || !selectedUser) return null;

        // Dostępni użytkownicy - bez wybranego użytkownika
        const availableParents = users.filter(u => u.id !== selectedUser.id);

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
                    <h3 className="text-xl font-bold mb-4">
                        Zmień podmiot nadrzędny
                    </h3>
                    <p className="mb-4 text-gray-600">
                        Użytkownik: <strong>{selectedUser.full_name || selectedUser.username}</strong>
                    </p>

                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2">Podmiot nadrzędny:</label>
                        <select
                            value={selectedParentId}
                            onChange={(e) => setSelectedParentId(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg"
                        >
                            <option value="">-- Brak (usuń powiązanie) --</option>
                            {availableParents.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.full_name || user.username}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex gap-3">
                        <button
                            onClick={handleSetParent}
                            className="flex-1 bg-softTeal text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition"
                        >
                            Zapisz
                        </button>
                        <button
                            onClick={() => {
                                setShowParentModal(false);
                                setSelectedUser(null);
                            }}
                            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition"
                        >
                            Anuluj
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen p-4">
            <main className="container mx-auto">
                <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl sm:text-2xl font-bold">Lista członków spółdzielni</h2>
                        <button
                            className="bg-softTeal text-pureWhite font-semibold px-4 py-2 rounded-lg hover:bg-warmOrange transition flex items-center"
                            onClick={() => navigate('/admin/users/create')}
                        >
                            <FaPlus className="mr-2" /> Dodaj
                        </button>
                    </div>

                    {loading ? (
                        <p className="text-center py-4">Ładowanie użytkowników...</p>
                    ) : error ? (
                        <p className="text-red-500 text-center py-4">{error}</p>
                    ) : users.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">Brak użytkowników</p>
                    ) : (
                        <>
                            <div className="md:hidden">
                                {renderMobileUserList()}
                            </div>

                            <div className="hidden md:block">
                                {renderDesktopTable()}
                            </div>
                        </>
                    )}
                </div>
            </main>

            {renderParentModal()}
        </div>
    );
};

export default ManageUsers;
